import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearRegistration,
  selectAuthErrorMessage,
  selectCurrentUser,
  selectIsInTeams,
  setRegisterAuthType
} from '@shared/store/auth.duck';
import { Redirect } from 'react-router';
import { selectIsLoading, selectIsSuccessful } from '../../store/registration.duck';
import AuthWrapper from './components/AuthWrapper';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { DialogIdentifiers, openDialog } from '@shared/store/dialog.duck';
import { AuthType, clearLoginStorage } from "@shared/utils/AuthUtils";
import { clearLastPath, getParameterByName } from "@shared/utils/UrlUtils";
import { RegisterErrorMessage } from '@shared/ui/organisms/auth/RegisterErrorMessage';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Colours from '@shared/ui/atoms/Colours';
import { Trans, useTranslation } from "react-i18next";
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';


export default function Register() {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const isLoading = useSelector(selectIsLoading);
  const isSuccessful = useSelector(selectIsSuccessful);

  useEffect(() => {
    document.body.style.backgroundColor = '#FAFAFA';
    clearLastPath();
    return () => {
      dispatch(clearRegistration());
    }
  }, [dispatch]);

  useEffect(() => {
    try {
      // @ts-ignore
      if (window.gtag) {
        // @ts-ignore
        window.gtag('config', 'AW-10781300539');
        // @ts-ignore
        window.gtag('event', 'conversion', {'send_to': 'AW-10781300539/5vTlCL7M_LgZELuu9pQo'});

      }
    } catch (e) {
    }
  }, []);

  if (currentUser) {
    return <Redirect to="/whereabouts/team"/>
  }

  if (isSuccessful) {
    return <Success/>
  }

  if (isLoading) {
    return <Processing/>
  }

  return <RegistrationForm/>
}

function RegistrationForm() {
  const dispatch = useDispatch();
  const errorMessage = useSelector(selectAuthErrorMessage);
  const isInTeams = useSelector(selectIsInTeams);

  const registerWithMs = () => {
    dispatch(setRegisterAuthType(AuthType.Microsoft));
    dispatch(openDialog(DialogIdentifiers.SignUpTermsDialog));
  }

  const registerWithGoogle = async (response: any) => {
    dispatch(setRegisterAuthType(AuthType.Google));
    dispatch(openDialog(DialogIdentifiers.SignUpTermsDialog));
  }

  const appendToLink = useMemo(() => {
    const ad = getParameterByName('ad', window.location.href);
    if (ad) {
      return '?ad=' + ad;
    }
    return ''
  }, []);

  useEffect(() => {
    clearLoginStorage();
  }, []);

  return (
    <AuthWrapper dataTest="register" title="register.title">

      <RegisterErrorMessage errorMessage={errorMessage} />

      <BodyRegular style={{marginBottom: 24}}><Trans i18nKey={'register.best-experience-signup'} /></BodyRegular>

      <OutlineButton dataTest="outlookLogin"
                     className="login__button"
                     text="register.office365"
                     click={registerWithMs}
                     borderColour={Colours.black}
                     fullWidth={true}
                     size={ButtonSize.Large}
                     imageUrl="/assets/icons/microsoft-office-logo.svg"/>

      {!isInTeams && <OutlineButton dataTest="googleLogin"
                                                          borderColour={Colours.black}
                                                          className="login__button"
                                                          text="register.google"
                                                          click={registerWithGoogle}
                                                          fullWidth={true}
                                                          size={ButtonSize.Large}
                                                          imageUrl="/assets/icons/google-logo.svg"/>}

      <OutlineButton text="register.email"
                     link={`/register/team-today${appendToLink}`}
                     borderColour={Colours.black}
                     className="login__button--teamToday"
                     dataTest="signUpWithPassword"
                     imageUrl="/assets/icons/font/Email.svg"
                     fullWidth={true}
                     size={ButtonSize.Large} />
    </AuthWrapper>
  )
}

function Processing() {
  return (
    <AuthWrapper dataTest="register-processing" spinner={true}>
    </AuthWrapper>
  )
}

function Success() {
  const {t} = useTranslation();
  return (
    <AuthWrapper dataTest="register-success" title="login.email-sent" icon={IconTypes.Tick}>
      <BodyRegular>{t('register.follow-the-link')}</BodyRegular>
    </AuthWrapper>
  )
}
